<template>
  <div>
    <a-card class="breadcrumb-card">
      <a-row>
        <a-col :span="device === 'mobile' ? 24 : 12" style="padding-left: 12px">
          <breadcrumb :resource="{}">
            <template v-slot:end>
              <span>
                <a-button
                  :loading="loading"
                  style="margin-bottom: 5px"
                  shape="round"
                  size="small"
                  @click="fetchData({ irefresh: true })">
                  <template #icon><sync-outlined /></template>
                  {{ $t('label.refresh') }}
                </a-button>
              </span>
            </template>
          </breadcrumb>
        </a-col>
        <a-col
          :span="device === 'mobile' ? 24 : 12"
          :style="device === 'mobile' ? { float: 'right', 'margin-top': '12px', 'margin-bottom': '-6px', display: 'table' } : { float: 'right', display: 'table', 'margin-top': '6px' }" >
          <span :style="styleSearch">
            <span class="filter-group">
              <a-input-search
                allowClear
                class="input-search"
                :placeholder="$t('label.search')"
                v-model:value="searchQuery"
                @search="onSearch">
                <template #addonBefore>
                  <a-popover
                    placement="bottomRight"
                    trigger="click"
                    v-model:visible="visibleFilter">
                    <template #content v-if="visibleFilter">
                      <a-form
                        style="min-width: 170px"
                        :ref="formRef"
                        :model="form"
                        :rules="rules"
                        layout="vertical"
                        @finish="handleSubmit"
                        v-ctrl-enter="handleSubmit">
                        <a-form-item
                          v-for="(field, index) in fields"
                          :ref="field.name"
                          :name="field.name"
                          :key="index"
                          :label="retrieveFieldLabel(field.name)">
                          <a-select
                            allowClear
                            v-if="field.type==='list'"
                            v-model:value="form[field.name]"
                            showSearch
                            :dropdownMatchSelectWidth="false"
                            optionFilterProp="label"
                            :filterOption="(input, option) => {
                              return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }"
                            :loading="field.loading"
                            @input="onchange($event, field.name)"
                            @change="onSelectFieldChange(field.name)">
                            <a-select-option
                              v-for="(opt, idx) in field.opts"
                              :key="idx"
                              :value="opt.id"
                              :label="$t(opt.name)">
                              <div>
                                <span v-if="(field.name.startsWith('zone'))">
                                  <span v-if="opt.icon">
                                    <resource-icon :image="opt.icon.base64image" size="1x" style="margin-right: 5px"/>
                                  </span>
                                  <global-outlined v-else style="margin-right: 5px" />
                                </span>
                                <span v-if="(field.name.startsWith('domain') || field.name === 'account')">
                                  <span v-if="opt.icon">
                                    <resource-icon :image="opt.icon.base64image" size="1x" style="margin-right: 5px"/>
                                  </span>
                                  <block-outlined v-else style="margin-right: 5px" />
                                </span>
                                {{ $t(opt.name) }}
                              </div>
                            </a-select-option>
                          </a-select>
                        </a-form-item>

                        <div class="filter-group-button">
                          <a-button
                            class="filter-group-button-clear"
                            type="default"
                            size="small"
                            @click="onClear">
                            <template #icon><stop-outlined /></template>
                            {{ $t('label.reset') }}
                          </a-button>
                          <a-button
                            class="filter-group-button-search"
                            type="primary"
                            size="small"
                            ref="submit"
                            html-type="submit">
                            <template #icon><search-outlined /></template>
                            {{ $t('label.search') }}
                          </a-button>
                        </div>
                      </a-form>
                    </template>
                    <a-button
                      class="filter-button"
                      size="small"
                      @click="() => { searchQuery = null }">
                      <filter-two-tone v-if="isFiltered" />
                      <filter-outlined v-else />
                    </a-button>
                  </a-popover>
                </template>
              </a-input-search>
            </span>
          </span>
        </a-col>
      </a-row>
    </a-card>
    <a-spin :spinning="loading">
      <table-view
        :columns="columns"
        :data="dataSource"
        rowKey="id" />
      <a-modal
        :title="this.$t('label.quota.set')"
        :visible="setModalVisible"
        :confirm-loading="setModalConfirmLoading"
        @ok="setQuotaCustom"
        @cancel="setModalClose"
        :width="'60%'">
        <a-form
          :model="editedUsage"
          :rules="rules"
          layout="vertical"
        >
          <table class="a-table" style="width: 100%; table-layout: fixed; text-align: left;">
            <thead>
              <tr>
                <th>{{ $t('label.quota.custom.domain') }}</th>
                <th>{{ $t('label.quota.custom.account') }}</th>
                <th>{{ $t('label.quota.custom.zone') }}</th>
                <th>{{ $t('label.quota.custom.type') }}</th>
                <th>{{ $t('label.quota.custom.typeUnit') }}</th>
                <th>{{ $t('label.quota.custom.unitsUsed') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ editedUsage.domain }}</td>
                <td>{{ editedUsage.account }}</td>
                <td>{{ editedUsage.zone }}</td>
                <td>{{ editedUsage.typeDescription }}</td>
                <td>{{ editedUsage.typeUnit }}</td>
                <td>
                  <a-input-number
                    v-model:value="editedUsage.unitsUsed"
                    :min="0"
                    :step="1"
                    :precision="0"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </a-form>
      </a-modal>
    </a-spin>
  </div>
</template>

<script>
import { ref, reactive, toRaw } from 'vue'
import { getAPI } from '@/api'
import { mixinDevice } from '@/utils/mixin.js'
import Breadcrumb from '@/components/widgets/Breadcrumb'
import TableView from '@/components/view/TableView'
import ResourceIcon from '@/components/view/ResourceIcon'
import TooltipButton from '@/components/widgets/TooltipButton'

export default {
  name: 'QuotaCustomUsage',
  components: {
    Breadcrumb,
    TableView,
    ResourceIcon,
    TooltipButton
  },
  mixins: [mixinDevice],
  props: {
    resource: {},
    tab: {
      type: String,
      default: () => ''
    }
  },
  data () {
    return {
      loading: false,
      dataSource: [],
      pattern: 'YYYY-MM-DD',
      searchFilters: ['zoneid', 'domainid', 'accountid'],

      searchQuery: null,
      paramsFilter: {},
      visibleFilter: false,
      fields: [],
      fieldValues: {},
      isFiltered: false,

      setModalVisible: false,
      setModalConfirmLoading: false,
      editedUsage: null,
      rules: null
    }
  },
  created () {
    this.formRef = ref()
    this.form = reactive({
      accountid: this.$store.getters.userInfo.accountid,
      domainid: this.$store.getters.userInfo.domainid
    })
    this.rules = reactive({})

    this.fetchData()
  },
  watch: {
    visibleFilter (newValue, oldValue) {
      if (newValue) {
        this.initFormFieldData()
      }
    },
    '$route' (to, from) {
      this.searchQuery = ''
      if (to && to.query && 'q' in to.query) {
        this.searchQuery = to.query.q
      }
      this.isFiltered = false
      this.searchFilters.some(item => {
        if (this.searchParams && this.searchParams[item]) {
          this.isFiltered = true
          return true
        }
      })
    }
  },
  mounted () {
    this.searchQuery = ''
    if (this.$route && this.$route.query && 'q' in this.$route.query) {
      this.searchQuery = this.$route.query.q
    }
  },
  computed: {
    columns () {
      return [
        {
          key: 'domain',
          title: this.$t('label.quota.custom.domain'),
          dataIndex: 'domain'
        },
        {
          key: 'account',
          title: this.$t('label.quota.custom.account'),
          dataIndex: 'account'
        },
        {
          key: 'zone',
          title: this.$t('label.quota.custom.zone'),
          dataIndex: 'zone'
        },
        {
          key: 'type',
          title: this.$t('label.quota.custom.type'),
          dataIndex: 'typeDescription'
        },
        {
          key: 'type',
          title: this.$t('label.quota.custom.typeUnit'),
          dataIndex: 'typeUnit'
        },
        {
          key: 'unitsUsed',
          title: this.$t('label.quota.custom.unitsUsed'),
          dataIndex: 'unitsUsed'
        },
        {
          key: 'actions',
          title: this.$t('label.actions'),
          customRender: (data) => {
            return (
              <a-space>
                <TooltipButton
                  tooltip={this.$t('label.edit')}
                  disabled={!('quotaCustomUsageSet' in this.$store.getters.apis)}
                  icon="edit-outlined"
                  onOnClick={() => this.setModalOpen(data.record)}
                />
                {this.$slots.default?.()}
              </a-space>
            )
          }
        }
      ]
    },
    styleSearch () {
      if (!this.searchFilters || this.searchFilters.length === 0) {
        return {
          width: '100%',
          display: 'table-cell'
        }
      }

      return {
        width: '100%',
        display: 'table-cell',
        lineHeight: '31px'
      }
    }
  },
  methods: {
    setQuotaCustom: async function (e) {
      e.preventDefault()
      const that = this

      that.setModalConfirmLoading = true
      getAPI('quotaCustomUsageSet', {
        zoneid: that.editedUsage.zoneId,
        domainid: that.editedUsage.domainId,
        accountid: that.editedUsage.accountId,
        usagetype: that.editedUsage.type,
        unitsused: that.editedUsage.unitsUsed
      }).then(async () => {
        that.$notification.success({ message: that.$t('label.quota.set.success') })
        that.setModalVisible = false
        that.fetchData()
      }).catch(function (error) {
        console.error(error)
        that.$notification.error({ message: error.response.data.quotacustomusagesetresponse.errortext })
      }).finally(() => {
        that.setModalConfirmLoading = false
      })
    },
    setModalClose: function () {
      this.setModalVisible = false
    },
    setModalOpen: function (record) {
      this.setModalVisible = true
      this.editedUsage = { ...record }
    },
    onchange: async function (event, fieldname) {
      this.fetchDynamicFieldData(fieldname, event.target.value)
    },
    onSelectFieldChange (fieldname) {
      if (fieldname === 'domainid') {
        this.fetchDynamicFieldData('accountid')
      }
    },
    onVisibleForm () {
      this.visibleFilter = !this.visibleFilter
      if (!this.visibleFilter) return
      this.initFormFieldData()
    },
    retrieveFieldLabel (fieldName) {
      if (fieldName === 'groupid') {
        fieldName = 'group'
      }
      if (fieldName === 'keyword') {
        if ('listAnnotations' in this.$store.getters.apis) {
          return this.$t('label.annotation')
        } else {
          return this.$t('label.name')
        }
      }
      return this.$t('label.' + fieldName)
    },
    initFields () {
      const arrayField = []

      this.fields = []
      this.searchFilters.forEach(item => {
        this.fields.push({
          type: 'list',
          name: item,
          opts: [],
          loading: false
        })
        arrayField.push(item)
      })

      return arrayField
    },
    async fetchDynamicFieldData (arrayField, searchKeyword) {
      const promises = []
      let zoneIndex = -1
      let domainIndex = -1
      let accountIndex = -1

      if (arrayField.includes('zoneid')) {
        zoneIndex = this.fields.findIndex(item => item.name === 'zoneid')
        this.fields[zoneIndex].loading = true
        promises.push(await this.fetchZones(searchKeyword))
      }
      if (arrayField.includes('domainid')) {
        domainIndex = this.fields.findIndex(item => item.name === 'domainid')
        this.fields[domainIndex].loading = true
        promises.push(await this.fetchDomains(searchKeyword))
      }
      if (arrayField.includes('accountid')) {
        accountIndex = this.fields.findIndex(item => item.name === 'accountid')
        this.fields[accountIndex].loading = true
        promises.push(await this.fetchAccounts(searchKeyword))
      }

      Promise.all(promises).then(response => {
        if (zoneIndex > -1) {
          const zones = response.filter(item => item.type === 'zoneid')
          if (zones && zones.length > 0) {
            this.fields[zoneIndex].opts = this.sortArray(zones[0].data)
          }
        }
        if (domainIndex > -1) {
          const domain = response.filter(item => item.type === 'domainid')
          if (domain && domain.length > 0) {
            this.fields[domainIndex].opts = this.sortArray(domain[0].data, 'path')
          }
        }
        if (accountIndex > -1) {
          const account = response.filter(item => item.type === 'accountid')
          if (account && account.length > 0) {
            this.fields[accountIndex].opts = this.sortArray(account[0].data, 'name')

            // changed domain
            if (arrayField === 'accountid') {
              this.form.accountid = this.fields[accountIndex].opts[0].id
            }
          }
        }
      }).finally(() => {
        if (zoneIndex > -1) {
          this.fields[zoneIndex].loading = false
        }
        if (domainIndex > -1) {
          this.fields[domainIndex].loading = false
        }
        if (accountIndex > -1) {
          this.fields[accountIndex].loading = false
        }
        if (Array.isArray(arrayField)) {
          this.fillFormFieldValues()
        }
      })
    },
    initFormFieldData () {
      const arrayField = this.initFields()

      this.fetchDynamicFieldData(arrayField)
    },
    sortArray (data, key = 'name') {
      if (!data) {
        return []
      }
      return data.sort(function (a, b) {
        if (a[key] < b[key]) { return -1 }
        if (a[key] > b[key]) { return 1 }

        return 0
      })
    },
    fillFormFieldValues () {
      if (Object.keys(this.$route.query).length > 0) {
        this.fieldValues = this.$route.query
      }
      if (this.$route.meta.params && Object.keys(this.$route.meta.params).length > 0) {
        Object.assign(this.fieldValues, this.$route.meta.params)
      }

      this.fields.forEach(field => {
        let defaultValue = ''
        if (field.name === 'domainid') {
          defaultValue = this.$store.getters.userInfo.domainid
        } else if (field.name === 'accountid') {
          defaultValue = this.$store.getters.userInfo.accountid
        } else if (field.name === 'zoneid' && field.opts.length > 0) {
          defaultValue = undefined
        }

        this.form[field.name] = this.fieldValues[field.name] || defaultValue
      })
    },
    fetchZones (searchKeyword) {
      return new Promise((resolve, reject) => {
        getAPI('listZones', { showicon: true, keyword: searchKeyword }).then(json => {
          const zones = json.listzonesresponse.zone
          resolve({
            type: 'zoneid',
            data: zones
          })
        }).catch(error => {
          reject(error.response.headers['x-description'])
        })
      })
    },
    fetchDomains (searchKeyword) {
      return new Promise((resolve, reject) => {
        getAPI('listDomains', { listAll: true, showicon: true, keyword: searchKeyword }).then(json => {
          const domain = json.listdomainsresponse.domain
          resolve({
            type: 'domainid',
            data: domain
          })
        }).catch(error => {
          reject(error.response.headers['x-description'])
        })
      })
    },
    fetchAccounts (searchKeyword) {
      return new Promise((resolve, reject) => {
        const params = { listAll: true, isrecursive: false, showicon: true, keyword: searchKeyword }
        if (this.form.domainid) {
          params.domainid = this.form.domainid
        }
        getAPI('listAccounts', params).then(json => {
          var account = json.listaccountsresponse.account
          if (this.form.domainid) {
            account = account.filter(a => a.domainid === this.form.domainid)
          }
          resolve({
            type: 'accountid',
            data: account
          })
        }).catch(error => {
          reject(error.response.headers['x-description'])
        })
      })
    },
    onSearch (value) {
      this.paramsFilter = {}
      this.searchQuery = value
    },
    onClear () {
      this.formRef.value.resetFields()
      this.form = reactive({})
      this.isFiltered = false
      this.searchQuery = null
      this.paramsFilter = {}
      this.$emit('search', this.paramsFilter)
    },
    async handleSubmit () {
      this.paramsFilter = {}
      this.formRef.value.validate().then(() => {
        const values = toRaw(this.form)
        this.isFiltered = true
        for (const key in values) {
          const input = values[key]
          if (input === '' || input === null || input === undefined) {
            continue
          }
          this.paramsFilter[key] = input
        }
      })

      this.fields.forEach(field => { this.fieldValues[field.name] = this.form[field.name] })
      await this.fetchData()
    },
    quotaCustomUsageList () {
      return new Promise((resolve, reject) => {
        const params = {
          domainid: this.form.domainid,
          accountid: this.form.accountid,
          zoneid: this.form.zoneid
        }

        getAPI('quotaCustomUsageList', params).then(json => {
          const quotaCustomUsage = json.quotacustomusagelistresponse.payload.quotaCustomUsage || {}
          resolve(quotaCustomUsage)
        }).catch(error => {
          reject(error)
        })
      })
    },
    async fetchData () {
      this.loading = true
      this.dataSource = []

      try {
        const quotaCustomUsage = await this.quotaCustomUsageList()

        this.dataSource = [...quotaCustomUsage]
        this.loading = false
      } catch (e) {
        console.error(e)
        this.loading = false
      }
    }
  }
}
</script>

<style scoped lang="less">
.input-search {
  margin-left: 10px;
}

.filter-group {
  :deep(.ant-input-group-addon) {
    padding: 0 5px;
  }

  &-button {
    background: inherit;
    border: 0;
    padding: 0;
  }

  &-button {
    position: relative;
    display: block;
    min-height: 25px;

    &-clear {
      position: absolute;
      left: 0;
    }

    &-search {
      position: absolute;
      right: 0;
    }
  }
}

.filter-button {
  background: inherit;
  border: 0;
  padding: 0;
  position: relative;
  display: block;
  min-height: 25px;
  width: 20px;
}
</style>
